import React, { Fragment, Component } from 'react';
import './SideBarSearch.scss';
import { SearchContext } from '../../context/SearchContext';
import { facets } from './facets/facetLogic';
import log from 'loglevel';
import { Field } from '../../types/Field';
import {
    Dismiss32Filled as CloseIcon,
    LineHorizontal320Filled as MenuIcon,
    ArrowLeft32Filled as ArrowLeftIcon,
} from '@fluentui/react-icons';
import { Anchor } from '../common/Anchor';
import { Button} from '@fluentui/react-components';
import FacetFilters from './facets/FecetFilters';
import { FacetValue, FacetValues, FacetValuesCount, isDefaultFacetFilter } from '../../types/Facet';
import { DisplayMode, DisplayModes, RenderFacetProps } from './facets/Types';

interface SideBarSearchProps {
    
}

/**
 * Represents the state for the SideBarSearch component.
 *
 * @property isPanelOpen - Indicates whether the side panel is currently open.
 * @property facetInFullDisplayMode - The name of the facet that is currently displayed in full view. So no other facets are shown in that case.
 */
interface SideBarSearchState {
    isPanelOpen: boolean;
    facetInFullDisplayMode: null | string;
}

class SideBarSearch extends Component<SideBarSearchProps, SideBarSearchState> {
    state = {
        isPanelOpen: false,
        facetInFullDisplayMode: null,
    };

    context!: React.ContextType<typeof SearchContext>;
    static contextType = SearchContext;

    toggleDisplayMode = (facetName: string, displayMode: DisplayMode) => {
        this.setState({ facetInFullDisplayMode: displayMode === DisplayModes.FULL ? facetName : null });
    };            

    clearGroupFilter = (facetName: string) => {        
        this.context.clearFilters([facetName]);
    };

    facetChanged = (facetName: string, valueChanged: FacetValue, checked: boolean) => {
        if (this.context.filterableUIFields === undefined || this.context.filterableUIFields.length === 0) {
            log.error('[facetChanged] No filterable UI fields found in the context.');
            return;
        }

        // Find the corresponding facet configuration by the name of the facet.
        const field = this.context.filterableUIFields.find((f) => f.name === facetName);
        if (!field) {
            log.error(`[facetChanged] No field found for facet name: ${facetName}`);
            return;
        }

        const facetLogic = facets.getFacetLogicByType(field.systemSettings.type);
        if (!facetLogic) {
            log.error(`[facetChanged] Unexpected data type found: ${field.systemSettings.type}`);
            return;
        }

        this.context.updateFacetSelection(facetName, valueChanged, facetLogic.createUpdatedSelection);
    };

    handleOpenFilter = () => {
        this.setState((prevState) => ({ isPanelOpen: !prevState.isPanelOpen }));
    };

    renderFacets = (fields: Field[], facetsValues: FacetValuesCount[], facetSelections: FacetValues<FacetValue>[], isExecutingSearch: boolean) => {        
        if (!fields || fields.length === 0 || !facetsValues || !facetSelections) {
            return null;
        }

        let renderedOutput: any[] = [];

        // The facets are rendered in the order they are returned in the facet configuration retrieved once from the back-end.
        fields.forEach((field) => {
            if (!field.userSettings.showInFacetArea) {
                return;
            }

            // Not all fields will return facet values, like Locations, for those will return an empty array.
            let facetValues: FacetValuesCount;
            if (!field.systemSettings.isFacetable ){
                facetValues = {
                    facetName: field.name,
                    facetValues: [],
                };
            }
            else{   

                const foundFacetValues = facetsValues.find((fv) => fv.facetName === field.name);
            
                if (!foundFacetValues) {                    
                    throw new Error(`No facet values found for field: ${field.name}`);                    
                }                

                facetValues =  foundFacetValues;

                if (facetValues.facetValues.length === 0){
                    return;
                }
            }                        

            let facetSelection = facetSelections.find((fs) => fs.facetName === field.name) || null;

            const renderFacetProps: RenderFacetProps = {
                field: field,
                facetValues: facetValues.facetValues,
                facetSelection: facetSelection,
                onChangeFacetSelection: this.facetChanged,
                toggleDisplayMode: this.toggleDisplayMode,
                displayMode: DisplayModes.COMPACT,
                isExecutingSearch: isExecutingSearch

            };
            renderedOutput.push(this.renderFacet(renderFacetProps));
        });

        return renderedOutput;
    };

    renderFacet = (props: RenderFacetProps) => {                
                        
        const facetLogic = facets.getFacetLogicByType(props.field.systemSettings.type);
        if (!facetLogic) {
            log.error(`[renderFacets] Unexpected data type found: ${props.field.systemSettings.type}`);
            return;
        }
                
        return facetLogic.renderFacet(props);       
    };

    getFacetFilterData = (facetSelections: FacetValues<FacetValue>[]) => {
       
        let filterDataArr: any[] = [];

        facetSelections.forEach((selection) => {
            let filterDataJson = {
                key: '',
                values: [] as string[],
                name: '',
            };

            if (selection.facetName !== 'TimeStart' && selection.facetName !== 'Duration') {
                 if (this.context.filterableUIFields === undefined || this.context.filterableUIFields.length === 0) {
                    throw new Error('[getFacetFilterData] No filterable UI fields found in the context.');
                }

                const field = this.context.filterableUIFields.find((f) => f.name === selection.facetName);
                if (!field) {
                    log.error(`[getFacetFilterData] No field found for facet name: ${selection.facetName}`);
                    return;
                }

                filterDataJson.key = field.systemSettings.displayName;
                filterDataJson.values = selection.facetValues.map((fv: FacetValue) => fv.getStringValue());
                filterDataJson.name = field.name;

                filterDataArr.push(filterDataJson);
            }
        });

        return filterDataArr;
    };

    render() {
        if(this.context.filterableUIFields === undefined)
            return;            

        let facetSelections2ShowInFilterView = this.context.facetSelections || [];

        // Filter out default facet filters, show we only show the filter view if there are non-default filters.
        facetSelections2ShowInFilterView = facetSelections2ShowInFilterView.filter((fs) => {
            const field = this.context.filterableUIFields!.find((f) => f.name === fs.facetName);
            if (!field) {
                throw new Error(`[SideBarSearch] No field found for facet name: ${fs.facetName}`);
            }

            return !isDefaultFacetFilter(field, fs);
        }); 

        let selectedFacetNames: string[] = facetSelections2ShowInFilterView.map((fs) => fs.facetName);

        let renderedFacets;
        if (this.state.facetInFullDisplayMode === null) {            
            renderedFacets = this.renderFacets(this.context.filterableUIFields, this.context.facetsValues, this.context.facetSelections, this.context.isExecutingSearch);
        }
        else{
            const fieldToShow = this.context.filterableUIFields.find((el: any) => el.name === this.state.facetInFullDisplayMode);
            if (!fieldToShow) {
                throw new Error(`[SideBarSearch] No field found for facet name: ${this.state.facetInFullDisplayMode}`);
            }

            const foundFacetValues = this.context.facetsValues.find((fv) => fv.facetName === fieldToShow.name);
            if (!foundFacetValues) {
                throw new Error(`[SideBarSearch] No facet values found for field: ${fieldToShow.name}`);
            }

            let facetSelection = this.context.facetSelections.find((fs) => fs.facetName === fieldToShow.name) || null;

            const renderFacetProps: RenderFacetProps = {
                field: fieldToShow,
                facetValues: foundFacetValues.facetValues,
                facetSelection: facetSelection,
                displayMode: DisplayModes.FULL,
                onChangeFacetSelection: this.facetChanged,
                toggleDisplayMode: this.toggleDisplayMode,
                isExecutingSearch: this.context.isExecutingSearch
            };

            renderedFacets = this.renderFacet(renderFacetProps);
        }

        const { isPanelOpen } = this.state;
        return (
            <Fragment>
                {facetSelections2ShowInFilterView.length !== 0 && (
                    <div className="side-header">
                        
                            <header className="wrapper-header display--flex">
                                <p className="title">Selected Filters</p>
                                <Anchor
                                    className="clear-filter-hl"
                                    onClick={() => {
                                        this.context.clearFilters(selectedFacetNames);
                                    }}
                                >
                                    Clear all filters
                                </Anchor>
                                <div className="support-dropdown-menu">
                                    <Button appearance="transparent" onClick={this.handleOpenFilter} icon={isPanelOpen ? <CloseIcon /> : <MenuIcon />} />
                                </div>
                            </header>
                        
                    </div>
                )}
                <div className={`selected-filters ${facetSelections2ShowInFilterView.length > 3 && 'selected-filters-height'}`}>
                    <FacetFilters selectedFacetsValues={facetSelections2ShowInFilterView} fields={this.context.filterableUIFields} clearGroupFilter={this.clearGroupFilter} />
                </div>
                {facetSelections2ShowInFilterView.length !== 0 && <hr className={`hr-rounded ${isPanelOpen && 'hr-rounded-show'} ${!isPanelOpen && 'hr-rounded-hide'}`}/>}
                <div className={`support-bar ${facetSelections2ShowInFilterView.length > 0 && 'support-bar-with-filter-height'}`}>
                    <section className={`support-search ${isPanelOpen && 'active'}`}>
                        {this.state.facetInFullDisplayMode === null ? (
                            renderedFacets
                        ) : 
                            <>
                                <ArrowLeftIcon onClick={() => this.toggleDisplayMode(this.state.facetInFullDisplayMode!, DisplayModes.COMPACT)} style={{ marginBottom: 20 }} />
                                {renderedFacets}
                            </>
                        }
                    </section>
                </div>
            </Fragment>
        );
    }
}

export default SideBarSearch;